<template>
  <div>
    <div v-if="error" class="error" v-html="error"></div>

    <header class="deal-flow-page--header">
      <div class="wrapper">
        <div class="deal-flow-page--header--title">
          <h1 v-if="page.acf.hero_title">{{ page.acf.hero_title }}</h1>
          <h1 v-else v-html="page.title.rendered"></h1>
        </div>
      </div>
    </header>

    <div class="wrapper">
      <form @submit.prevent="submit" class="form">
        <h1>Investment Application</h1>

        <FormGroup
          label="Title of investment"
          v-model="investment.title"
          :errors="errors.title"
          @errors="updateErrors"
          :required="true"
        />

        <FormGroup
          label="Description of the investment: explains what the investment is for"
          type="textarea"
          :max-length="250"
          v-model="investment.acf.summary"
          :errors="errors.summary"
          @errors="updateErrors"
          :required="true"
        />

        <FormGroup
          label="Type of investment"
          type="radio"
          :options="[
            'Equity (owning)',
            'Debt (lending)',
            'Asset investment (physical value)',
          ]"
          v-model="investment.acf.type"
          :errors="errors.type"
          @errors="updateErrors"
          :required="true"
        />

        <fieldset class="columns">
          <FormGroup
            label="Raise amount"
            prefix="$"
            v-model="investment.acf.raise_amount"
            :errors="errors.raise_amount"
            @errors="updateErrors"
            :required="true"
          />

          <FormGroup
            label="Minimum investment"
            prefix="$"
            v-model="investment.acf.minimum_investment"
            :errors="errors.minimum_investment"
            @errors="updateErrors"
            :required="true"
          />

          <FormGroup
            label="Target return"
            v-model="investment.acf.target_return"
            :errors="errors.target_return"
            @errors="updateErrors"
            :required="true"
          />

          <FormGroup
            label="Industry"
            v-model="investment.acf.industry"
            :errors="errors.industry"
            @errors="updateErrors"
            :required="true"
          />
        </fieldset>

        <h2>Investment Lead</h2>

        <fieldset class="columns">
          <FormGroup
            label="Company"
            v-model="investment.acf.company"
            :errors="errors.company"
            @errors="updateErrors"
            :required="true"
          />

          <FormGroup
            label="Name"
            v-model="investment.acf.company_contact"
            :errors="errors.company_contact"
            @errors="updateErrors"
            :required="true"
          />

          <FormGroup
            label="Phone"
            type="tel"
            v-model="investment.acf.company_contact_phone"
            :errors="errors.company_contact_phone"
            @errors="updateErrors"
            :required="true"
          />

          <FormGroup
            label="Email"
            type="email"
            v-model="investment.acf.company_contact_email"
            :errors="errors.company_contact_email"
            @errors="updateErrors"
            :required="true"
          />
        </fieldset>

        <p>
          Society does not broker securities or guarantee outcomes.
          Opportunities are shared for informational and relationship-building
          purposes only.
        </p>

        <div
          class="form-group form-group--file"
          :class="{ 'form-group--error': errors.pdf }"
        >
          <label for="pdf" class="btn lrg ght">
            <span class="loading" v-if="pdf_loading">
              <span class="spinner"></span>
              <span class="sr-only">Uploading...</span>
            </span>
            <template v-else-if="pdf_filename">{{ truncatedFilename }}</template>
            <template v-else>Upload Investment Deck</template>
          </label>

          <input
            type="file"
            id="pdf"
            class="sr-only"
            accept="application/pdf"
            @change="addPdf"
            :required="true"
          />

          <div v-if="errors.pdf" class="error-message">{{ errors.pdf }}</div>
        </div>

        <div class="form-footer">
          <button
            type="submit"
            class="btn lrg"
            :disabled="submitting || pdf_loading"
          >
            Submit Investment
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import FormGroup from '@/components/FormGroup.vue';
import DOMPurify from 'dompurify';

export default {
  name: 'NewInvestment',
  components: {
    FormGroup,
  },
  data() {
    return {
      page: null,
      loading: false,
      investment: {
        title: '',
        acf: {
          summary: '',
          raise_amount: '',
          minimum_investment: '',
          target_return: '',
          industry: '',
          company: '',
          company_contact: '',
          company_contact_phone: '',
          company_contact_email: '',
          type: null,
          pdf: null,
        },
      },
      pdf_filename: '',
      pdf_loading: false,
      submitting: false,
      errors: {},
    };
  },
  mounted() {
    this.$store.dispatch('getPage', 'deal-flow').then(() => {
      this.page = this.$store.getters.getPage('deal-flow');
      this.loading = false;
    });
  },
  computed: {
    error() {
      return this.$store.getters.getError;
    },
    user() {
      return this.$store.getters.getUser;
    },
    truncatedFilename() {
      const name = this.pdf_filename;
      const max = 30;
      if (name.length <= max) return name;
      const dot = name.lastIndexOf('.');
      const ext = dot > -1 ? name.slice(dot) : '';
      const base = dot > -1 ? name.slice(0, dot) : name;
      const keep = max - ext.length - 3;
      return `${base.slice(0, Math.max(keep, 1))}...${ext}`;
    },
  },
  methods: {
    addPdf(ev) {
      ev.preventDefault();
      delete this.errors.pdf;

      const file = ev.target.files[0];
      if (!file) return;

      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        this.errors.pdf = 'File size exceeds the maximum limit of 10MB.';
        this.$forceUpdate();
        return;
      }

      if (file.type !== 'application/pdf') {
        this.errors.pdf = 'Only PDF files are allowed.';
        this.$forceUpdate();
        return;
      }

      this.pdf_filename = file.name;
      this.pdf_loading = true;
      this.$store
        .dispatch('addMedia', file)
        .then((attachment) => {
          this.pdf_loading = false;
          if (!attachment) {
            this.errors.pdf = 'Error uploading PDF. Please try again.';
            return;
          }
          this.investment.acf.pdf = attachment.id;
        })
        .catch((err) => {
          this.pdf_loading = false;
          this.errors.pdf = err.message || 'Error uploading PDF.';
        });
    },
    updateErrors(errors) {
      if (errors[1] === false) {
        delete this.errors[errors[0]];
      } else {
        this.errors[errors[0]] = errors[1];
      }
    },
    submit() {
      this.$store.commit('setError', false);

      if (Object.keys(this.errors).length > 0) {
        this.$store.commit(
          'setError',
          'Please correct errors before submitting.'
        );
        return;
      }

      if (this.pdf_loading) {
        this.$store.commit(
          'setError',
          'Please wait for the PDF to finish uploading.'
        );
        return;
      }

      const payload = {
        title: DOMPurify.sanitize(this.investment.title),
        acf: {
          summary: DOMPurify.sanitize(this.investment.acf.summary || ''),
          type: this.investment.acf.type,
          raise_amount: this.investment.acf.raise_amount,
          minimum_investment: this.investment.acf.minimum_investment,
          target_return: DOMPurify.sanitize(
            this.investment.acf.target_return || ''
          ),
          industry: DOMPurify.sanitize(this.investment.acf.industry || ''),
          company: DOMPurify.sanitize(this.investment.acf.company || ''),
          company_contact: DOMPurify.sanitize(
            this.investment.acf.company_contact || ''
          ),
          company_contact_phone: DOMPurify.sanitize(
            this.investment.acf.company_contact_phone || ''
          ),
          company_contact_email: DOMPurify.sanitize(
            this.investment.acf.company_contact_email || ''
          ),
          pdf: this.investment.acf.pdf,
        },
      };

      this.submitting = true;
      this.$store
        .dispatch('submitInvestment', payload)
        .then((result) => {
          this.submitting = false;
          if (result) {
            this.$router.push({
              path: '/deal-flow',
              query: {
                message:
                  'Investment submitted. An admin will review it within a week.',
              },
            });
          }
        })
        .catch((err) => {
          this.submitting = false;
          this.$store.commit(
            'setError',
            err.message || 'Error submitting investment.'
          );
        });
    },
  },
};
</script>
